import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Slider, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  RemoveCircleOutline as RemoveIcon,
  AddCircleOutline as AddIcon,
  RestaurantMenu as ConsumeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PredictionService from '../../services/PredictionService';

const RecorderPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

function ConsumptionRecorder({ item, onConsumptionRecorded }) {
  const [open, setOpen] = useState(false);
  const [newQuantity, setNewQuantity] = useState(item ? Math.max(0, item.quantity - 1) : 0);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setNewQuantity(Math.max(0, item.quantity - 1)); // Default to current quantity minus 1
    setNotes('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleQuantityChange = (event, newValue) => {
    setNewQuantity(newValue);
  };

  const handleInputChange = (event) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value) && value >= 0 && value <= item.quantity) {
      setNewQuantity(value);
    }
  };

  const handleNotesChange = (event) => {
    setNotes(event.target.value);
  };

  const incrementQuantity = () => {
    if (newQuantity < item.quantity) {
      setNewQuantity(prev => Math.min(item.quantity, prev + 1));
    }
  };

  const decrementQuantity = () => {
    if (newQuantity > 0) {
      setNewQuantity(prev => Math.max(0, prev - 1));
    }
  };

  const handleSubmit = async () => {
    if (newQuantity >= item.quantity) {
      setError('New quantity must be less than current quantity');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      
      const result = await PredictionService.recordConsumption(
        item.item_id,
        newQuantity,
        notes || null
      );
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
          if (onConsumptionRecorded) {
            onConsumptionRecorded(result);
          }
        }, 1500);
      } else {
        setError(result.message || 'Failed to record consumption');
      }
    } catch (error) {
      console.error('Error recording consumption:', error);
      setError('Failed to record consumption. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!item) return null;

  return (
    <>
      <RecorderPaper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Record Consumption
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ConsumeIcon />}
            onClick={handleOpen}
          >
            Record Usage
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Record when you use this item to improve prediction accuracy
        </Typography>
      </RecorderPaper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Record Consumption
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {success ? (
            <Alert severity="success">
              Consumption recorded successfully!
            </Alert>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Typography variant="subtitle1" gutterBottom>
                {item.item_name}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current quantity: {item.quantity} {item.unit_of_measure}
                </Typography>
              </Box>
              
              <Typography variant="body2" gutterBottom>
                How much is left after consumption?
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={decrementQuantity} size="small">
                  <RemoveIcon />
                </IconButton>
                
                <TextField
                  value={newQuantity}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ 
                    min: 0, 
                    max: item.quantity,
                    step: 0.1
                  }}
                  sx={{ mx: 2, width: '100px' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{item.unit_of_measure}</InputAdornment>,
                  }}
                />
                
                <IconButton onClick={incrementQuantity} size="small">
                  <AddIcon />
                </IconButton>
              </Box>
              
              <Box sx={{ px: 2, mb: 3 }}>
                <Slider
                  value={newQuantity}
                  onChange={handleQuantityChange}
                  min={0}
                  max={item.quantity}
                  step={item.quantity > 10 ? 0.5 : 0.1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value} ${item.unit_of_measure}`}
                />
              </Box>
              
              <TextField
                label="Notes (optional)"
                value={notes}
                onChange={handleNotesChange}
                fullWidth
                multiline
                rows={2}
                placeholder="E.g., Used for dinner, Gave some to a neighbor, etc."
              />
            </>
          )}
        </DialogContent>
        {!success && (
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={isSubmitting || newQuantity >= item.quantity}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Recording...' : 'Record Consumption'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}

export default ConsumptionRecorder;